import { Button } from '@calizahq/react-components-v2'
import { WorkspaceView } from '@sales-app/context/workspace/workspace.type'
import { formatInTimeZone } from 'date-fns-tz'

import { Tag } from '@/components'
import { NotificationAlert } from '@/graphql/queries/notifications.queries'
import { NotifiableType, NotificationParentType } from '@/graphql/subscriptions/notifications.subscriptions'
import { useGuardedNavigate } from '@/hooks/useGuardedNavigate'
import { APP_TYPE } from '@/types/User'
import { APP_STORAGE_KEY } from '@/util'

export const NotificationsColumns = (
  handleMarkNotification: (notifiableId: number, notifiableType: NotifiableType) => void,
) => {
  return [
    {
      Header: 'Message',
      accessor: 'message',
      width: 70,
    },
    {
      Header: 'Type',
      accessor: 'notifiableType',
      width: 30,
    },
    {
      Header: 'Time',
      accessor: 'createdAt',
      width: 30,
      Cell: function CellRenderer({ value = '' }: { value: string }) {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        try {
          const time = formatInTimeZone(value, timezone, 'yyyy-MM-dd, h:mmaaa')
          return time
        } catch (e) {
          return 'Invalid  Date'
        }
      },
    },
    {
      Header: 'Status',
      accessor: 'viewedAt',
      Cell: ({ value }: any) => {
        return <Tag variant={!!value ? 'info' : 'none'}>{!!value ? 'Read' : 'Not Read'}</Tag>
      },
      width: 30,
    },
    {
      accessor: 'id',
      width: 30,
      Cell: function CellRenderer({ row }: { row: { original: NotificationAlert } }) {
        const { notifiableId, notifiableType, viewedAt } = row.original

        const routePath = notificationPath(row.original)
        const navigate = useGuardedNavigate()

        function routeToNotification() {
          if (!viewedAt) handleMarkNotification(notifiableId, notifiableType)
          if (routePath) navigate(routePath)
        }

        return (
          <div className="ml-auto flex items-center justify-center">
            <Button className=" bg-text-semantic-success" onClick={() => routeToNotification()} size="small">
              View
            </Button>
          </div>
        )
      },
    },
  ]
}

export const notificationPath = (notificationAlert) => {
  const active_app = localStorage.getItem(APP_STORAGE_KEY)
  if (notificationAlert.parent) {
    switch (notificationAlert.parent.parentType) {
      case NotificationParentType.Corporate: {
        return `/active-workspace/corporate/${notificationAlert.parent.parentId}/${WorkspaceView.SUMMARY}`
      }
      case NotificationParentType.Member: {
        switch (active_app) {
          case APP_TYPE.SALES: {
            return `/active-workspace/member/${notificationAlert.parent.parentId}/${WorkspaceView.SUMMARY}`
          }
          case APP_TYPE.SUPPORT: {
            return `/members/${notificationAlert.parent.parentId}`
          }
          default: {
            return ''
          }
        }
      }
      case NotificationParentType.Lead: {
        return `/active-workspace/lead/${notificationAlert.parent.parentId}/${WorkspaceView.SUMMARY}`
      }
      case NotificationParentType.Ticket: {
        return `/tickets/${notificationAlert.parent.parentId}#${notificationAlert.notifiableId}`
      }
      default: {
        return ''
      }
    }
  } else {
    switch (notificationAlert.notifiableType) {
      case NotifiableType.TicketComment: {
        return `/tickets/${notificationAlert.parentTicketId}#${notificationAlert.notifiableId}`
      }
      case NotifiableType.Ticket: {
        return `/tickets/${notificationAlert.notifiableId}`
      }
      case NotifiableType.PotentialBooking: {
        return `/active-workspace/${WorkspaceView.POTENTIALBOOKING}/${notificationAlert.notifiableId}`
      }
      default: {
        return ''
      }
    }
  }
}

export const notificationLabel = (notificationAlert) => {
  switch (notificationAlert.notifiableType) {
    case NotifiableType.Comment: {
      return 'New Comment'
    }
    case NotifiableType.TicketComment: {
      return 'New Ticket Comment'
    }
    case NotifiableType.Ticket: {
      return 'New Ticket'
    }
    case NotifiableType.PotentialBooking: {
      return 'New Potential Booking'
    }
    default: {
      return ''
    }
  }
}

// isDeprecatedPath: the caller (Sidebar) already knows whether v2SupportDeprecated
// is on and whether this notification's target falls under it — this runs outside
// React (a subscription callback), so it can't read the flag itself via a hook.
export const onNotificationClick = (notificationAlert, isDeprecatedPath = false) => {
  if (isDeprecatedPath) return undefined

  const routePath = notificationPath(notificationAlert)
  switch (notificationAlert.notifiableType) {
    case NotifiableType.Agent: {
      return undefined
    }
    default: {
      return (e) => {
        e.preventDefault()
        window.open(`${window.location.origin}${routePath}`, '_blank')
      }
    }
  }
}
